import { get, pick } from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import { DateTime } from 'luxon'
import { storageMode } from './storage'
import { PLATFORM_PAY } from './booking'

/**
 * getBranchAttribution - reads stored Branch attribution from localStorage
 * @returns {object|null}
 */
export const getBranchAttribution = () => {
  try {
    const raw = storageMode('localStorage').getItem('branch_attribution')
    return raw ? JSON.parse(raw) : null
  } catch (e) {
    console.warn('getBranchAttribution:', e)
    return null
  }
}

/**
 * Standard UTM keys, captured from the URL and forwarded as user traits on
 * identify so Mixpanel profiles carry the UTMs as well as the click IDs
 */
export const UTM_KEYS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
]

/**
 * URL attribution params to capture from landing page query strings
 */
const URL_ATTRIBUTION_PARAMS = [
  ...UTM_KEYS,
  // Ad platform click IDs
  'gclid', 'gbraid', 'wbraid',  // Google Ads
  'fbclid',                       // Meta/Facebook
  'ttclid',                       // TikTok
  'msclkid',                      // Microsoft/Bing
  'epik',                         // Pinterest
  'sclid',                        // Snapchat
  'twclid',                       // Twitter/X
  'li_fat_id',                    // LinkedIn
  // Email platforms
  '_kx',                          // Klaviyo
  'mc_cid', 'mc_eid',            // Mailchimp
  // Custom (agency-configured Google Ads tracking template)
  'tw_source', 'tw_adid', 'tw_campaign', 'tw_kwdid',
]

/**
 * Click ID keys forwarded as user traits on identify
 */
export const CLICK_ID_KEYS = [
  'gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid',
  'msclkid', 'epik', 'sclid', 'twclid', 'li_fat_id',
  '_kx', 'mc_cid', 'mc_eid',
  'tw_source', 'tw_adid', 'tw_campaign', 'tw_kwdid',
]

/**
 * getUrlAttribution - reads UTM params and ad click IDs from the current URL
 * @returns {object|null}
 */
const getUrlAttribution = () => {
  try {
    if (typeof window === 'undefined') {
      return null
    }

    const params = new URLSearchParams(window.location.search)
    const attribution = {}
    let hasAny = false

    URL_ATTRIBUTION_PARAMS.forEach(key => {
      const value = params.get(key)
      if (value) {
        attribution[key] = value
        hasAny = true
      }
    })

    return hasAny ? attribution : null
  } catch (e) {
    console.warn('getUrlAttribution:', e)
    return null
  }
}

// attribution from the URL this page load landed on, kept in memory so the
// Segment page call made when Segment loads can carry the campaign even after
// a page such as /book has rewritten the URL
let landingAttribution = null

/**
 * captureUrlAttribution - captures UTM params and ad click IDs from the landing URL
 * A tagged URL replaces the stored attribution outright (last touch), so tags
 * from two different visits never end up in one record. An untagged URL leaves
 * the stored attribution alone.
 */
export const captureUrlAttribution = () => {
  try {
    const urlAttribution = getUrlAttribution()
    if (!urlAttribution) {
      return
    }

    landingAttribution = urlAttribution
    storageMode('localStorage').setItem(
      'ad_attribution',
      JSON.stringify(urlAttribution)
    )
  } catch (e) {
    console.warn('captureUrlAttribution:', e)
  }
}

/**
 * getAdAttribution - reads the current URL attribution, falling back to the
 * stored attribution when the URL is untagged. The URL wins whole rather than
 * being merged, matching captureUrlAttribution.
 * @returns {object|null}
 */
export const getAdAttribution = () => {
  try {
    const urlAttribution = getUrlAttribution()
    if (urlAttribution) {
      return urlAttribution
    }

    const raw = storageMode('localStorage').getItem('ad_attribution')
    const storedAttribution = raw ? JSON.parse(raw) : null

    return storedAttribution && Object.keys(storedAttribution).length
      ? storedAttribution
      : null
  } catch (e) {
    console.warn('getAdAttribution:', e)
    return null
  }
}

/**
 * landingCampaignContext - Segment options carrying the campaign from the URL
 * this page load landed on, for the page call made once Segment loads
 * @returns {object|undefined} `{ context: { campaign } }`, or undefined when
 *   the landing URL was untagged
 */
export const landingCampaignContext = () =>
  buildCampaignContext(null, landingAttribution)

/**
 * inferAdSource - infer ad source from click ID when utm_source is missing
 */
const inferAdSource = (ad) => {
  if (ad.gclid || ad.gbraid || ad.wbraid) { return 'google' }
  if (ad.fbclid) { return 'facebook' }
  if (ad.ttclid) { return 'tiktok' }
  if (ad.msclkid) { return 'microsoft' }
  if (ad.epik) { return 'pinterest' }
  if (ad.sclid) { return 'snapchat' }
  if (ad.twclid) { return 'twitter' }
  if (ad.li_fat_id) { return 'linkedin' }
  if (ad._kx) { return 'klaviyo' }
  if (ad.mc_cid) { return 'mailchimp' }
  if (ad.tw_source) { return ad.tw_source }
  return undefined
}

/**
 * buildCampaignContext - maps Branch and/or ad attribution to Segment context.campaign
 * Branch takes priority over ad attribution (more specific)
 * @param {object} branchAttribution
 * @param {object} adAttribution
 * @returns {object|undefined} { context: { campaign: { name, source, medium, content, term } } }
 */
export const buildCampaignContext = (branchAttribution, adAttribution) => {
  const branch = branchAttribution || {}
  const ad = adAttribution || {}

  const campaign = {
    name: branch['~campaign'] || ad.utm_campaign || ad.tw_campaign,
    source: branch['~channel'] || ad.utm_source || inferAdSource(ad),
    medium: branch['~feature'] || ad.utm_medium,
    content: ad.utm_content,
    term: ad.utm_term,
  }

  // don't return empty campaign
  if (!Object.values(campaign).some(Boolean)) {
    return undefined
  }

  return {
    context: {
      campaign,
    }
  }
}

/**
 * googleTagManagerEvent, track an event for google tag manager
 * @param {string} eventName
 */
export const googleTagManagerEvent = (event, data) => {
  if (typeof window === 'undefined') {
    return
  }
  if (!window.dataLayer || !event) {
    return
  }

  window.dataLayer.push({
    event,
    ...data
  })
}

/**
 * trackTTQ: tracking for tiktok pixel
 *
 * @param {*} url
 */
export const trackTTQ = eventName => {
  if (typeof window === 'undefined') {
    return
  }
  if (!window.ttq) {
    return
  }
  window.ttq.track(eventName)
}

/**
 * segmentPageView: record a pageview
 *
 * @param {string} path
 * @param {object} [options] Segment options, e.g. `{ context: { campaign } }`
 */
export const segmentPageView = (path, options) => {
  if (typeof window === 'undefined') {
    return
  }
  if (!window.analytics) {
    return
  }
  window.analytics.page(path, {}, options)
  segmentEvent('Page View', { path }, options)
}
/**
 * segmentIdentify: identifies a user. Calls made before Segment has loaded
 * are queued alongside track events and replayed in order by
 * flushSegmentQueue, so a signed-in visitor
 * whose profile loads within Segment's ~2s injection delay still sends their
 * attribution traits.
 *
 * @param {*} id
 * @param {*} user
 * @param {object} [options] Segment options, e.g. `{ context: { campaign } }`
 * @param {function} [onSent] passed to analytics.js as the identify callback,
 *   so it runs once the SDK has processed the call (not when the pre-load
 *   snippet stub merely queued it). Use it to clear stored attribution, so a
 *   visitor who leaves before Segment finishes loading keeps it for their next
 *   visit.
 */
export const segmentIdentify = (id, user, options, onSent) => {
  if (typeof window === 'undefined') {
    return
  }
  if (!window.analytics) {
    // same queue as track events, so replay keeps identify/track order and
    // each queued event stays with the account it was fired for
    if (_segmentQueue.length < SEGMENT_QUEUE_MAX) {
      _segmentQueue.push({ type: 'identify', id, user, options, onSent })
    }
    return
  }

  window.analytics.identify(id, {
    external_id: id,
    ...user
  }, options, onSent)
}

let _segmentAnonymousId
/**
 * sets and returns the current segment anon id
 *
 * @export
 * @return {*} 
 */
export function segmentAnonymousId() {
  const savedAnonymousId = localStorage.getItem('segmentAnonymousId')

  if (savedAnonymousId) {
    _segmentAnonymousId = savedAnonymousId
  } else {
    _segmentAnonymousId = uuidv4()
    localStorage.setItem('segmentAnonymousId', _segmentAnonymousId)
  }

  window?.analytics?.setAnonymousId(_segmentAnonymousId)
  return _segmentAnonymousId
}

/**
 * googleAnalyticsEvent: record an event
 *
 * @param {*} url
 */
export const googleAnalyticsEvent = (action, attributes, options) => {
  gtag('event', action, attributes, options)
}

/**
 * googleAnalyticsSet: set a value 
 * 
 */
export const googleAnalyticsSet = (action, attributes) => {
  gtag('set', action, attributes)
}

/**
 * generateSessionId - generates a session id and stores it in session storage
 *
 * @return {*} 
 */
const generateSessionId = () => {
  let sessionId = window.sessionStorage.getItem('sessionId')
  if (!sessionId) {
    sessionId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
    window.sessionStorage.setItem('sessionId', sessionId)
  }
  return sessionId
}

// Segment is injected lazily (see ExternalScripts.js), so window.analytics does
// not exist for the first couple seconds after load. Events fired before then —
// notably the GrowthBook 'Experiment Viewed' exposure, which fires as soon as
// features load — would otherwise be silently dropped. Queue them and replay
// once Segment is ready (flushSegmentQueue is called from ExternalScripts).
let _segmentQueue = []
// cap so a session where Segment never loads (e.g. skipped bot IPs) can't grow unbounded
const SEGMENT_QUEUE_MAX = 100

/**
 * pageContext: the Segment `context.page` shape for the current document.
 * Captured when an event is QUEUED so a later replay is attributed to the page
 * the event actually happened on, not whatever page the client had moved to by
 * the time Segment loaded (the 'Experiment Viewed' exposure showed up on the
 * homepage/FAQ for that reason).
 */
const pageContext = () => ({
  path: window.location.pathname,
  referrer: document.referrer,
  search: window.location.search,
  title: document.title,
  url: window.location.href
})

export const flushSegmentQueue = () => {
  const queued = _segmentQueue
  _segmentQueue = []
  // replay in call order: identifies (see segmentIdentify) and track events
  // share the queue, so each event follows the identify it was fired after
  queued.forEach(entry => {
    if (entry.type === 'identify') {
      segmentIdentify(entry.id, entry.user, entry.options, entry.onSent)
      return
    }
    const { action, attributes, options, page } = entry
    segmentEvent(action, attributes, { ...options, context: { ...options?.context, page } })
  })
}

const SEGMENT_BATCH_URL = 'https://api.segment.io/v1/batch'

/**
 * segmentBeacon: deliver events straight to Segment's HTTP API with
 * navigator.sendBeacon, bypassing analytics.js. The browser guarantees beacon
 * delivery even after the page unloads, which analytics.js cannot — so this is
 * the path for anything that must survive a bounce: the queued events below
 * when the client leaves before Segment has loaded, and events that are by
 * definition fired while leaving (trackBookingFlowAbandonedDuringLoad).
 *
 * Payloads mirror what analytics.js sends (see segmentEvent) so downstream
 * destinations see the same shape; `context.library` marks them as beaconed.
 * Body is text/plain, like analytics.js, so no CORS preflight is needed.
 *
 * @param {Array<{action: string, attributes?: object, options?: object, page?: object}>} events
 *   an event's `options.anonymousId` overrides the tab's id, as it does in
 *   analytics.js, so a queued exposure keeps the id it was bucketed on
 * @returns {boolean} whether the beacon was accepted by the browser
 */
export const segmentBeacon = events => {
  try {
    if (typeof window === 'undefined' || typeof navigator === 'undefined' || !navigator.sendBeacon) {
      return false
    }
    const writeKey = process.env.NEXT_PUBLIC_SEGMENT_KEY
    if (!writeKey || !events?.length) {
      return false
    }
    const anonymousId = segmentAnonymousId()
    const sentAt = new Date().toISOString()
    const batch = events.map(({ action, attributes, options, page }) => ({
      type: 'track',
      event: action,
      properties: {
        ...attributes,
        is_authenticated: false,
        platform: 'web',
        sessionId: generateSessionId()
      },
      anonymousId: options?.anonymousId || anonymousId,
      context: {
        page: page || pageContext(),
        userAgent: navigator.userAgent,
        library: { name: 'cicada-beacon', version: '1' }
      },
      timestamp: sentAt,
      messageId: `beacon-${uuidv4()}`
    }))
    const body = new Blob([JSON.stringify({ batch, writeKey, sentAt })], { type: 'text/plain' })
    return navigator.sendBeacon(SEGMENT_BATCH_URL, body)
  } catch (error) {
    console.error(error, 'segmentBeacon')
    return false
  }
}

/**
 * segmentBeaconEvent: fire one event via segmentBeacon (see above). Use for
 * events emitted while the page is being left; use segmentEvent for everything
 * else so the normal analytics.js pipeline (identify, middleware, bundled
 * destinations) applies.
 */
export const segmentBeaconEvent = (action, attributes) => {
  if (typeof window === 'undefined') {
    return false
  }
  return segmentBeacon([{ action, attributes, page: pageContext() }])
}

/**
 * flushSegmentQueueViaBeacon: the client is leaving before Segment loaded, so
 * anything still queued would be lost. Beacon it instead. Called from the
 * pagehide/visibilitychange listeners installed on first queue use.
 */
export const flushSegmentQueueViaBeacon = () => {
  if (typeof window === 'undefined' || window.analytics) {
    return false
  }
  // the beacon carries track events only; queued identifies stay queued for a
  // client who comes back, and if they never do, the attribution they carry
  // was never cleared from storage, so the next visit sends it
  const trackEvents = _segmentQueue.filter(entry => entry.type !== 'identify')
  if (!trackEvents.length) {
    return false
  }
  const sent = segmentBeacon(trackEvents)
  if (sent) {
    _segmentQueue = _segmentQueue.filter(entry => entry.type === 'identify')
  }
  return sent
}

/**
 * addDocumentLeaveListener: run `handler` when the client leaves the document.
 *
 * `pagehide` on its own is not enough, and is close to useless on mobile:
 * switching apps, locking the screen or moving to another tab fires
 * `visibilitychange` -> hidden, and the page may then be frozen or discarded
 * without `pagehide` ever running. Anything that has to go out as the client
 * leaves — a beacon, a queue flush — must listen for both.
 *
 * The handler can therefore be called more than once, and for a client who
 * comes back. Callers that must report once per document need their own guard.
 *
 * @param {function} handler - called when the document is hidden or unloaded
 * @returns {function} removes both listeners
 */
export const addDocumentLeaveListener = handler => {
  if (typeof window === 'undefined') {
    return () => {}
  }
  // These run while the page is going away, where an uncaught error is both
  // hard to observe and able to stop later listeners on the same event from
  // running -- including the Segment queue flush.
  const safeHandler = () => {
    try {
      handler()
    } catch (error) {
      console.error(error, 'addDocumentLeaveListener')
    }
  }
  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      safeHandler()
    }
  }
  window.addEventListener('pagehide', safeHandler)
  document.addEventListener('visibilitychange', onVisibilityChange)
  return () => {
    window.removeEventListener('pagehide', safeHandler)
    document.removeEventListener('visibilitychange', onVisibilityChange)
  }
}

let _beaconListenersInstalled = false
const installSegmentQueueBeacon = () => {
  if (_beaconListenersInstalled || typeof window === 'undefined') {
    return
  }
  _beaconListenersInstalled = true
  addDocumentLeaveListener(flushSegmentQueueViaBeacon)
}

/**
 * segmentEvent: record an event. Queued until Segment is ready (see flushSegmentQueue).
 *
 * @param {string} action - the event name
 * @param {object} [attributes] - event properties
 * @param {object} [options] - Segment options (integrations, context, ...)
 */
export const segmentEvent = (action, attributes, options) => {

  try {
    if (typeof window === 'undefined') {
      return
    }
    if (!window.analytics) {
      if (_segmentQueue.length < SEGMENT_QUEUE_MAX) {
        _segmentQueue.push({ action, attributes, options, page: pageContext() })
        installSegmentQueueBeacon()
      }
      return
    }

    // rokt asked us to include email in all events so grab the identified user
    const email = typeof window.analytics?.user === 'function' ? window.analytics?.user?.()?.traits?.()?.email : undefined

    window.analytics.track(action, {
      ...attributes,
      email,
      is_authenticated: !!email,
      platform: 'web',
      sessionId: generateSessionId()
    }, {
      integrations: {
        'Facebook Pixel': {
          eventID: attributes?.checkout_id ?? attributes?.id  // ← This is what Facebook uses for deduplication
        }
      },
      ...options,
    })
  } catch (error) {
    console.error(error, 'segmentEvent')
  }
}

/**
 * googleAnalyticsPageView: record a pageview
 *
 * @param {*} url
 */
export const googleAnalyticsPageView = path => {
  googleAnalyticsEvent('page_view', {
    page_path: path
  })
}

const gtag = (...args) => {
  if (typeof window === 'undefined') {
    return
  }
  if (!window.gtag) {
    console.warn('gtag not found')
    return
  }
  window.gtag(...args)
}

/**
 * generateBookingEvent, parses booking info, used with trackBooking for google analytics
 * @param {*} bookedAppointment
 */

const generateBookingEvent = bookedAppointment => {
  return {
    id: bookedAppointment.id,
    revenue: bookedAppointment.appointmentServices
      .reduce(function (sum, item) {
        var price = item.service.price
        if (bookedAppointment.address.market?.tip) {
          price += price * parseFloat(bookedAppointment.address.market?.tip)
        }
        sum = sum + price
        return sum
      }, 0)
      .toFixed(2),
    tax: bookedAppointment.appointmentServices
      .reduce(function (sum, item) {
        var tax = 0
        if (bookedAppointment.address.market?.serviceTax) {
          tax =
            item.service.price *
            parseFloat(bookedAppointment.address.market?.serviceTax)
        }
        sum = sum + tax
        return sum
      }, 0)
      .toFixed(2),
    shipping: '',
    currency: 'US',
    items: bookedAppointment.appointmentServices.map(function (
      appointmentService
    ) {
      var name = appointmentService.service.name.replace(/\//g, '')
      return {
        id: bookedAppointment.id,
        price: appointmentService.service.price.toFixed(2),
        quantity: 1,
        name: name,
        sku: name.replace(/ /g, '').toUpperCase(),
        type: appointmentService.service.serviceType.name
      }
    })
  }
}

const serviceToTrackingItem = marketService => {
  var name = marketService.service.name.replace(/\//g, '')
  return {
    item_name: name,
    item_id: name.replace(/ /g, '').toUpperCase(),
    price: marketService.price.toFixed(2),
    item_brand: 'GLAMSQUAD',
    item_category: marketService.service.serviceType.name,
    quantity: 1
  }
}

export const trackViewServiceList = (market, marketServices) => {
  try {
    if (!market) {
      return false
    }

    const data = {
      items: (marketServices || []).map(serviceToTrackingItem),
      item_list_name: `${market?.name} Services`,
      item_list_id: `${market?.shortName}_services`
    }

    googleAnalyticsEvent('view_item_list', data)
    segmentEvent('view_item_list', data)
    trackProductListViewed('All', marketServices)
  } catch (error) {
    console.warn('trackViewServiceList:', error)
  }
}

/** 
 * trackAddressLookupFailed: track an out of zone address lookup failure
 */
export const trackAddressLookupFailed = (address) => {
  const data = {
    address,
    timestamp: new Date().toISOString()
  };
  googleAnalyticsEvent('address_lookup_failed', data);
  segmentEvent('Address Lookup Failed', data);
};

export const trackSelectService = (market, marketService) => {
  try {
    if (!market) {
      return false
    }

    const data = {
      items: serviceToTrackingItem(marketService),
      item_list_name: `${market?.name} Services`,
      item_list_id: `${market?.shortName}_services`
    }

    googleAnalyticsEvent('select_item', data)
    segmentEvent('select_item', data)
    trackProductViewed(marketService)
  } catch (error) {
    console.warn('trackSelectService:', error)
  }
}

export const trackAddServiceToCart = (market, marketService, instances, addOnMarketServices) => {
  try {
    if (!market) {
      return false
    }

    const data = {
      items: serviceToTrackingItem(marketService),
      item_list_name: `${market?.name} Services`,
      item_list_id: `${market?.shortName}_services`
    }

    googleAnalyticsEvent('add_to_cart', data)
    segmentEvent('add_to_cart', data)
    trackProductAdded(marketService, instances, addOnMarketServices)
    trackTTQ('AddToCart')
  } catch (error) {
    console.warn('trackAddServiceToCart:', error)
  }
}

export const trackRemoveServiceFromCart = (market, marketService, serviceRequest) => {
  try {
    if (!market) {
      return false
    }

    const data = {
      items: serviceToTrackingItem(marketService),
      item_list_name: `${market?.name} Services`,
      item_list_id: `${market?.shortName}_services`
    }

    googleAnalyticsEvent('remove_from_cart', data)
    trackProductRemoved(marketService, serviceRequest)
  } catch (error) {
    console.warn('trackRemoveServiceFromCart:', error)
  }
}

/**
 * generatePurchaseEvent: formats event attributes for GA4 ecommerce purchase event
 *
 * @param {*} bookedAppointment
 * @returns
 */
const generatePurchaseEvent = bookedAppointment => {
  const bookingEvent = generateBookingEvent(bookedAppointment)  
  
  return {
    transaction_id: bookingEvent.id,
    value: bookingEvent.revenue,
    tax: bookingEvent.tax,
    currency: 'USD',
    items: bookedAppointment.appointmentServices.map(aps =>
      serviceToTrackingItem(aps)
    )
  }
}

/**
 * generateEventData, parses booking info, used with trackEvent for google tag manager
 * @param {*} bookedAppointment
 */

const generateEventData = (bookedAppointment, isNewUser) => {
  const serviceItems = bookedAppointment.appointmentServices.map(as => {
    return {
      sku: as.service.name,
      price: as.service.price,
      type: 'Service',
      serviceType: as.service.serviceType.name
    }
  })

  return {
    data: {
      id: bookedAppointment.id,
      price: bookedAppointment.totalPrice,
      userType: isNewUser ? 'new' : 'repeat',
      items: serviceItems,
      email: bookedAppointment.owner.email
    }
  }
}

/**
 * trackLeadGen, parses booking info to track lead gen for rakuten
 * @param {*} bookedAppointment
 */

const trackLeadGen = (
  bookedAppointment,
  transaction,
  isNewUser,
  promoCode,
  promoCodeAmount
) => {
  try {
    var rm_trans = {
      affiliateConfig: {
        ranMID: process.env.NEXT_PUBLIC_RAKUTEN_RAN_MID,
        discountType: 'item',
        includeStatus: 'false'
      },
      customerStatus: isNewUser ? 'New' : 'Existing',
      customerID: bookedAppointment.ownerId,
      discountCode: promoCode,
      discountAmount: promoCodeAmount,
      orderid: bookedAppointment.id,
      currency: 'USD',
      taxAmount: bookedAppointment.taxAmount,
      conversionType: 'Sale',
      lineitems: transaction.items.map(item => ({
        quantity: 1,
        SKU: item.sku,
        productName: item.name,
        unitPrice: item.price * 1
      }))
    }

    // prettier-ignore
    /* eslint-disable */
    /*Do not edit any information beneath this line*/
    !function (a, b, c) {
      var d = a.rakutenDataLayerName || "DataLayer"; a[d] = a[d] || {}, a[d].events = a[d].events || {}, a[d].events.SPIVersion = "3.4.1", a[d].Sale = a[d].Sale || {}, a[d].Sale.Basket = a[d].Sale.Basket || {}, c.Ready = a[d].Sale.Basket.Ready && a[d].Sale.Basket.Ready + 1 || 1, a[d].Sale.Basket = c;
      var e = function (a) { for (var c, d = a + "=", e = b.cookie.split(";"), f = 0; f < e.length; f++) { for (c = e[f]; " " == c.charAt(0);)c = c.substring(1, c.length); if (0 == c.indexOf(d)) return c.substring(d.length, c.length) } return "" }, f = function (a) { var b = a || "", c = {}; if (a || (b = e("rmStore")), b) { for (; b !== decodeURIComponent(b);)b = decodeURIComponent(b); for (var d = b.split("|"), f = 0; f < d.length; f++)c[d[f].split(":")[0]] = d[f].split(":")[1] } return c }, g = {}; g = f(); var h = function (a, b, c, d) { c = c || "", d = d || {}; var e = g[a || ""], f = d[b || ""], h = d.ignoreCookie || !1; e = h ? 0 : e; var i = e || f || c; return i = ("string" != typeof i || "false" !== i.toLowerCase()) && i, i }, k = function (a, c, d, e, f) { var g = b.createElement(a), h = -1 < b.location.protocol.indexOf("s") ? "https:" : "http:"; for (var i in c = c.replace("https:", h), g.src = c, e = e || {}, "script" == a ? e.type = e.type || "text/javascript" : (e.style = "display:none;", "img" == a && (e.alt = "", e.height = "1", e.width = "1")), e) e.hasOwnProperty(i) && g.setAttribute(i, e[i]); var j = b.getElementsByTagName(d); j = j.length ? j[0] : b.getElementsByTagName("script")[0].parentElement, f && (g.onload = f, g.onreadystatechange = function () { ("complete" == this.readyState || "loaded" == this.readyState) && f() }), j.appendChild(g) }, l = function (a) { var b = new Date, c = b.getUTCFullYear() + ("0" + (b.getUTCMonth() + 1)).slice(-2) + ("0" + b.getUTCDate()).slice(-2) + ("0" + b.getUTCHours()).slice(-2) + ("0" + b.getUTCMinutes()).slice(-2); return "NoOID_" + (a ? a + "_" : "") + Math.round(1e5 * Math.random()) + "_" + c }, m = function () { var b = a[d] && a[d].Sale && a[d].Sale.Basket ? a[d].Sale.Basket : {}, c = c || b.affiliateConfig || {}, f = h("amid", "ranMID", "", c) || b.ranMID; if (!f) return !1; var g = "undefined" == typeof c.allowCommission || "false" !== c.allowCommission; if (!g) return !1; if (!b.orderid && !(b.lineitems && b.lineitems.length)) return !1; var m = h("adn", "domain", "track.linksynergy.com", c), o = h("atm", "tagType", "pixel", c), p = h("adr", "discountType", "order", c), q = h("acs", "includeStatus", "false", c), r = h("arto", "removeOrderTax", "false", c), s = h("artp", "removeTaxFromProducts", "false", c), t = h("artd", "removeTaxFromDiscount", "false", c), u = h("atr", "taxRate", b.taxRate || 0, c), v = h("ald", "land", !1, {}) || (c.land && !0 === c.land ? e("ranLandDateTime") : c.land) || !1, w = h("atrv", "tr", !1, {}) || (c.tr && !0 === c.tr ? e("ranSiteID") : c.tr) || !1, x = h("acv", "centValues", "true", c), y = h("ancc", "nonCentCurrencies", "JPY", c); u = Math.abs(+u); var z = (100 + u) / 100, A = b.orderid || l(f); A = encodeURIComponent(A); var B = b.currency || ""; B = encodeURIComponent(B.toUpperCase()); var C = !1; if (B && y) { y = (y + "").split(","); for (var D = 0; D < y.length; D++)y[D] == B && (C = !0) } var F = function (a) { return C && (a = Math.round(a)), x && "false" !== x ? (a *= 100, a = Math.round(a)) : a = Math.round(100 * a) / 100, a + "" }, G = b.taxAmount ? Math.abs(+b.taxAmount) : 0, H = b.discountAmount ? Math.abs(+b.discountAmount) : 0, I = b.discountAmountLessTax ? Math.abs(+b.discountAmountLessTax) : 0; !I && H && t && u && (I = H / z), I = I || H; var J = "ep"; "mop" === o && (J = "eventnvppixel"); var K = (b.customerStatus || "") + "", L = ""; K && (q && "EXISTING" == K.toUpperCase() || q && "RETURNING" == K.toUpperCase()) && (L = "R_"); for (var M = [], N = 0, O = 0; O < (b.lineitems ? b.lineitems.length : 0); O++)if (b.lineitems[O]) { var P = !1, Q = a.JSON ? JSON.parse(JSON.stringify(b.lineitems[O])) : b.lineitems[O], R = +Q.quantity || 0, S = +Q.unitPrice || 0, T = +Q.unitPriceLessTax, U = T || S || 0; s && u && !T && (U /= z); for (var V, W = R * U, X = 0; X < M.length; X++)V = M[X], V.SKU === Q.SKU && (P = !0, V.quantity += R, V.totalValue += W); P || (Q.quantity = R, Q.totalValue = W, M.push(Q)), N += W } for (var Y = "", Z = "", $ = "", _ = "", aa = {}, O = 0; O < M.length; O++) { var Q = M[O], ba = encodeURIComponent(Q.SKU), ca = Q.totalValue, R = Q.quantity, da = encodeURIComponent(Q.productName) || ""; "item" === p.toLowerCase() && I && (ca -= I * ca / N); var ea = Q.optionalData; for (var fa in ea) ea.hasOwnProperty(fa) && (aa[fa] = aa[fa] || "", aa[fa] += encodeURIComponent(ea[fa]) + "|"); Y += L + ba + "|", Z += R + "|", $ += F(ca) + "|", _ += L + da + "|" } Y = Y.slice(0, -1), Z = Z.slice(0, -1), $ = $.slice(0, -1), _ = _.slice(0, -1), I && (I = F(I)), G && (G = F(G)), I && "order" === p.toLowerCase() && (Y += "|" + L + "DISCOUNT", _ += "|" + L + "DISCOUNT", Z += "|0", $ += "|-" + I), r && G && (Y += "|" + L + "ORDERTAX", Z += "|0", $ += "|-" + G, _ += "|" + L + "ORDERTAX"); var ga = "https://" + m + "/" + J + "?mid=" + f; ga += "&ord=" + A, ga += v ? "&land=" + v : "", ga += w ? "&tr=" + w : "", ga += "&cur=" + B, ga += "&skulist=" + Y, ga += "&qlist=" + Z, ga += "&amtlist=" + $, ga += "&img=1", ga += "&spi=" + a[d].events.SPIVersion, I && "item" === p.toLowerCase() && (ga += "&discount=" + I); var ea = b.optionalData || {}; for (var fa in b.discountCode && (ea.coupon = b.discountCode), b.customerStatus && (ea.custstatus = b.customerStatus), b.customerID && (ea.custid = b.customerID), I && (ea.disamt = I), ea) ea.hasOwnProperty(fa) && (ga += "&" + encodeURIComponent(fa) + "=" + encodeURIComponent(ea[fa])); for (var fa in aa) aa.hasOwnProperty(fa) && (ga += "&" + encodeURIComponent(fa) + "list=" + aa[fa].slice(0, -1), I && "order" === p.toLowerCase() && (ga += "|"), G && r && (ga += "|")); ga += "&namelist=" + _; if (8000 < ga.length) { for (var ha = 8000; 0 < ha;)if ("&" == ga.charAt(ha)) { ga = ga.slice(0, ha); break } else ha--; ga += "&trunc=true" } k("img", ga, "body") }, n = function () { var b = a[d] && a[d].Sale && a[d].Sale.Basket ? a[d].Sale.Basket : {}, c = c || b.displayConfig || {}, e = h("dmid", "rdMID", "", c); if (!e) return !1; if (!b.orderid && !b.conversionType) return !1; var f = h("dtm", "tagType", "js", c), g = h("ddn", "domain", "tags.rd.linksynergy.com", c), j = h("dis", "includeStatus", "false", c), m = h("dcomm", "allowCommission", "notset", c), n = h("duup", "useUnitPrice", "false", c), o = h("drtp", "removeTaxFromProducts", "false", c), p = h("drtd", "removeTaxFromDiscount", "false", c), q = h("dtr", "taxRate", b.taxRate || 0, c), r = ""; "true" === m || !0 === m || "1" === m || 1 === m ? r = "1" : ("false" === m || !1 === m || "0" === m || 0 === m) && (r = "0"), f = "js" === f || "if" === f || "img" === f ? f : "js"; var s = "script"; "if" === f ? s = "iframe" : "img" === f && (s = "img"), ("true" === n || !0 === n || "1" === n || 1 === n) && (n = !0); var t = (b.customerStatus || "") + "", u = ""; t && j && ("EXISTING" == t.toUpperCase() || "RETURNING" == t.toUpperCase()) && (u = "R_"); var v = b.orderid; v || (v = l((b.conversionType + "").toLowerCase() + "_" + e)), v = encodeURIComponent(u + v); var w = encodeURIComponent(b.currency || ""), x = 0, y = ""; q = Math.abs(+q); var z = (100 + q) / 100, A = b.discountAmount ? Math.abs(+b.discountAmount) : 0, B = b.discountAmountLessTax ? Math.abs(+b.discountAmountLessTax) : 0; !B && A && p && q && (B = A / z), B = B || A, B = isNaN(B) ? 0 : B; for (var C = 0; C < (b.lineitems ? b.lineitems.length : 0); C++)if (b.lineitems[C]) { var D = +b.lineitems[C].quantity, E = +b.lineitems[C].unitPriceLessTax * D; (!E || n) && (o && q ? E = +b.lineitems[C].unitPrice / z * D : E = +b.lineitems[C].unitPrice * D), E = isNaN(E) ? 0 : E, x += E, y += encodeURIComponent(b.lineitems[C].SKU) + "," } x = Math.round(100 * (x - B)) / 100, y = y.slice(0, -1); var F = "https://" + g + "/" + f + "/" + e; F += "/?pt=" + "conv", F += "&orderNumber=" + v, F += "&spi=" + a[d].events.SPIVersion, x && (F += "&price=" + x), w && (F += "&cur=" + w), r && (F += "&tvalid=" + r), y && (F += "&prodID=" + y), k(s, F, "body") }, o = function () { var b = a[d] && a[d].Sale && a[d].Sale.Basket ? a[d].Sale.Basket : {}, c = b.searchConfig || {}, e = 1024, f = encodeURIComponent("...TRUNCATED"), g = h("smid", "rsMID", "", c); if (!g) return !1; var j = h("said", "accountID", "113", c), m = h("sclid", "clickID", "", c), n = encodeURIComponent(h("sct", "conversionType", b.conversionType && "sale" !== (b.conversionType + "").toLowerCase() ? b.conversionType : "conv", c)); k("script", "https://services.xg4ken.com/js/kenshoo.js?cid=" + g, "body", null, function () { var a = {}; if (a.conversionType = n, a.revenue = 0, a.currency = encodeURIComponent(b.currency || "USD"), a.orderId = encodeURIComponent(b.orderid || l(n)), a.promoCode = encodeURIComponent(b.discountCode || ""), m && (a.ken_gclid = encodeURIComponent(m)), a.discountAmount = +(b.discountAmount || 0), a.discountAmount = isNaN(a.discountAmount) ? 0 : Math.abs(a.discountAmount), a.customerStatus = encodeURIComponent(b.customerStatus || ""), a.productIDList = "", a.productNameList = "", b.lineitems && b.lineitems.length) { for (var c = 0; c < b.lineitems.length; c++)b.lineitems[c] && (a.revenue += +(b.lineitems[c].unitPrice || 0) * +b.lineitems[c].quantity, a.productIDList += (b.lineitems[c].SKU || "NA") + ",", a.productNameList += (b.lineitems[c].productName || "NA") + ","); a.revenue = Math.round(100 * (a.revenue - a.discountAmount)) / 100, a.productIDList = encodeURIComponent(a.productIDList.slice(0, -1)), a.productNameList = encodeURIComponent(a.productNameList.slice(0, -1)), a.productIDList.length > e && (a.productIDList = a.productIDList.substring(0, e - f.length) + f), a.productNameList.length > e && (a.productNameList = a.productNameList.substring(0, e - f.length) + f) } kenshoo.trackConversion(j, g, a) }) }; a[d].SPI = { readRMCookie: e, processRMStoreCookie: f, readRMStoreValue: h, sRAN: m, sDisplay: n, sSearch: o, addElement: k, rmStore: g }, m(), n(), o()
    }(window, document, rm_trans);
    /* eslint-enable */

  } catch (error) {
    console.warn('trackLeadGen:', error)
  }
}

/**
 * Tracks the lifecycle of a payment authentication (3D Secure / SCA) challenge
 * during booking: 'presented' when the bank challenge is shown, then one of
 * 'completed', 'declined' or 'failed' for its outcome. The subsequent re-booking
 * still fires the normal Booking / Booking Failed events.
 *
 * 'declined' means the issuer refused the card (`reason` is the Stripe error code,
 * `declineCode` the `decline_code` — e.g. `do_not_honor`, `insufficient_funds`).
 * Those payments would have failed with or without 3D Secure, so the challenge
 * completion rate is completed / (presented - declined - cancelled), NOT
 * completed / presented. ('cancelled' is never emitted on web — see below — but
 * belongs in the denominator for any funnel spanning both platforms.)
 * Lumping declines into 'failed' is what made this funnel read as a 100% 3DS
 * failure rate in July 2026 when the implementation was working correctly.
 *
 * Mirrors the mantis app's funnel, which still emits only 'completed'/'failed'
 * (with an uninformative literal "Failed" reason) and so remains subject to that
 * same conflation. The 'cancelled' value exists for schema parity with mantis but
 * is never emitted on web, where Stripe.js cannot distinguish a user-cancel from
 * a failed authentication.
 *
 * @param {'presented'|'completed'|'cancelled'|'declined'|'failed'} result - challenge stage/outcome.
 * @param {{reason?: string, declineCode?: string, paymentIntentId?: string, market?: string, paymentMethod?: string}} [details]
 */
export const trackBookingChallenge = (result, details = {}) => {
  segmentEvent('Booking Challenge', { result, ...details })
}

/**
 *  trackBooking, track a new booking for google analytics, google tag manager, and rakuten.
 * @param {*} bookedAppointment
 */
export const trackBooking = (
  bookedAppointment,
  isNewUser,
  promoCode,
  promoCodeAmount,
  marketServicesForMarket = [],
  serviceRequests = [],
  market,
  user,
  { fromBookedServices = false } = {},
) => {
  try {
    // GTM
    const data = generateEventData(bookedAppointment, isNewUser)
    googleTagManagerEvent('book', data)
    trackTTQ('CompletePayment')

    // send GA4 purchase event
    const purchaseEventData = {
      ...generatePurchaseEvent(bookedAppointment),
      market: market?.shortName,
      ...pick(user, ['email', 'nameFirst', 'nameLast', 'phone']),
      ...pick(bookedAppointment?.address, ['city', 'state', 'zip']),
    }

    googleAnalyticsEvent('purchase', purchaseEventData)
    
    googleAnalyticsEvent(`purchase_${get(data, 'data.userType')}`, purchaseEventData)

    // alternate ecommerce event (used by GTM/Facebook integration?)
    const transaction = generateBookingEvent(bookedAppointment)

    gtag('require', 'ecommerce')

    googleAnalyticsEvent('ecommerce:addTransaction', {
      id: transaction.id,
      revenue: transaction.revenue,
      tax: transaction.tax
    })

    transaction.items.forEach(function (item) {
      googleAnalyticsEvent('ecommerce:addItem', {
        id: item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        sku: item.sku
      })
    })

    googleAnalyticsEvent('ecommerce:send')

    // RAKUTEN
    // if (process.env.NODE_ENV === 'production') {
    //   trackLeadGen(bookedAppointment);
    // }

    trackLeadGen(
      bookedAppointment,
      transaction,
      isNewUser,
      promoCode,
      promoCodeAmount
    )

    const hoursInAdvance = Math.floor(DateTime.fromSeconds(bookedAppointment.actualStartDateTime).diffNow('hours').hours)

    trackOrderCompleted({ ...bookedAppointment, hoursInAdvance, isNewUser }, marketServicesForMarket, serviceRequests, { fromBookedServices })
  } catch (error) {
    console.warn('trackBooking:', error)
  }
}

/**
 * trackBookingLocationOpened - tracks the location picker being opened from the
 * straight-to-service booking flow, so we can measure how often shoppers check
 * or switch their market before selecting services.
 *
 * @param {object} market - the market the user is currently booking in
 */
export const trackBookingLocationOpened = (market) => {
  segmentEvent('Booking Location Opened', {
    market: market?.shortName,
  })
}

/**
 * trackBookingMarketChanged - tracks a commit that changes the booking market
 * via the location picker.
 *
 * @param {object} params
 * @param {object} params.from - the market being switched away from
 * @param {object} params.to - the market being switched to
 * @param {'address'|'market-button'|'saved-address'} params.method - how the new market was chosen
 */
export const trackBookingMarketChanged = ({ from, to, method }) => {
  segmentEvent('Booking Market Changed', {
    fromMarket: from?.shortName,
    toMarket: to?.shortName,
    method,
  })
}

/**
 * trackBookingFailed - tracks a failed booking attempt to segment (matches mantis)
 *
 * `paymentMethod` is normalized here to the app's BookingPaymentMethod enum
 * ('platformPay' | 'card') so cross-platform Segment/Mixpanel filters line up —
 * mantis derives the same values in book-button.tsx. Callers pass the raw
 * selection (a Stripe payment-method id, null, or the PLATFORM_PAY sentinel);
 * anything other than the sentinel is a card booking.
 *
 * @param {string} errorType - failure stage/category ('payment', 'api', '3dSecure_*', ...)
 * @param {string} errorMessage
 * @param {object} appointment
 * @param {object} market
 * @param {string|null} selectedPaymentMethodId - raw selection; normalized before sending
 * @param {object} [platformPayAvailability] - reported device wallets for outcome attribution
 */
export const trackBookingFailed = (errorType, errorMessage, appointment, market, selectedPaymentMethodId, platformPayAvailability) => {
  try {
    segmentEvent('Booking Failed', {
      errorType,
      errorMessage,
      walletType: checkoutPaymentContext(selectedPaymentMethodId, platformPayAvailability).walletType,
      totalPrice: appointment?.finalPriceWithTaxAndTip,
      paymentMethod: selectedPaymentMethodId === PLATFORM_PAY ? 'platformPay' : 'card',
      market: market?.shortName,
    })
  } catch (error) {
    console.warn('trackBookingFailed:', error)
  }
}

/**
 * trackBookingHeal - tracks the outcome of an automatic booking-failure
 * recovery ("heal"). After the booking endpoint rejects with a healable
 * validation error (searchExpired / providerBooked), the flow re-runs the
 * availability search and either silently rebooks the same slot with fresh
 * booking tokens or returns the customer to the search step — see
 * helpers/bookingHeal.js.
 *
 * A 'returned_to_search' heal is always accompanied by a Booking Failed event
 * (the attempt did fail); a 'rebooked' heal is not — the booking succeeded and
 * fires Order Completed, and this event is the only record a failure occurred.
 *
 * `selectedPaymentMethodId` is the raw selection and is normalized to
 * 'platformPay' | 'card' exactly like trackBookingFailed, so the two events
 * join cleanly in cross-platform funnels.
 *
 * @param {'rebooked'|'returned_to_search'} outcome
 * @param {{failureType: string, appointment: object, market: object, selectedPaymentMethodId: string|null}} details
 */
export const trackBookingHeal = (
  outcome,
  { failureType, appointment, market, selectedPaymentMethodId } = {}
) => {
  try {
    segmentEvent('Booking Heal', {
      outcome,
      failureType,
      totalPrice: appointment?.finalPriceWithTaxAndTip,
      paymentMethod: selectedPaymentMethodId === PLATFORM_PAY ? 'platformPay' : 'card',
      market: market?.shortName,
    })
  } catch (error) {
    console.warn('trackBookingHeal:', error)
  }
}

/**
 * sends a Lead event to facebook w/ content_category
 *
 * @param {*} contentCategory
 */
export const trackFacebookLead = () /* contentCategory */ => {
  // try {
  //   if (typeof window === 'undefined') {
  //     return
  //   }
  //   const { default: ReactPixel } = require('react-facebook-pixel');
  //   if (!ReactPixel?.track) {
  //     return
  //   }
  //   ReactPixel.track('Lead', { content_category: contentCategory })
  // } catch (error) {
  //   console.warn('trackFacebookLead:', error)
  // }
}

export const trackCheckout = data => {
  try {
    segmentEvent('checkout', data)
    googleAnalyticsEvent('checkout', data)
    trackTTQ('CompletePayment')
    trackOrderCompleted(data)
  } catch (error) {
    console.warn('trackCheckout:', error)
  }

}

/**
  * object containing currently used standard events 
  * that segment supports 
  * https://segment.com/docs/connections/spec/b2b-saas
  * https://segment.com/docs/connections/spec/ecommerce/v2/#event-lifecycles
  */
export const segmentStandardEvents = {
  PRODUCT_LIST_VIEWED: 'Product List Viewed',
  PRODUCT_CLICKED: 'Product Clicked',
  PRODUCT_VIEWED: 'Product Viewed',
  PRODUCT_ADDED: 'Product Added',
  PRODUCT_REMOVED: 'Product Removed',
  CHECKOUT_STARTED: 'Checkout Started',
  ORDER_COMPLETED: 'Order Completed',
  PAYMENT_INFO_ENTERED: 'Payment Info Entered',
  AVAILABILITY_SEARCH: 'Availability Search',
  SIGNED_UP: 'Signed Up',
  SIGNED_IN: 'Signed In',
  EXPERIMENT_VIEWED: 'Experiment Viewed'
}

/**
 * trackExperimentViewed: record a GrowthBook experiment exposure.
 *
 * Called from the GrowthBook trackingCallback (services/growthbook.js) so each
 * user's variant assignment is recorded and can be joined to the auth funnel
 * events (Signin Started/Shown, Signed In, etc.). Uses camelCase property names
 * to mirror the mantis app.tsx trackingCallback so both apps' exposures unify in
 * Segment. segmentEvent no-ops safely if window.analytics is not yet ready.
 *
 * When the experiment hashes on anonymousId, the exposure is sent under the
 * exact id GrowthBook bucketed (`result.hashValue`) rather than whatever id
 * Segment holds when the event finally goes out. The two can differ: the
 * exposure may wait in the pre-Segment queue, and segmentAnonymousId() is a
 * read-then-write on shared localStorage, so two documents starting in the
 * same instant (link scanners detonating a campaign URL) each bucket on their
 * own id while analytics.js re-reads the last one written. GrowthBook joins
 * exposures on anonymous_id, so without this a unit shows up in both arms and
 * is dropped as a multiple exposure.
 */
export const trackExperimentViewed = (experiment, result) => {
  const bucketedAnonymousId =
    result?.hashAttribute === 'anonymousId' ? result?.hashValue : undefined

  segmentEvent(
    segmentStandardEvents.EXPERIMENT_VIEWED,
    {
      experimentId: experiment?.key,
      variationId: result?.key
    },
    bucketedAnonymousId ? { anonymousId: bucketedAnonymousId } : undefined
  )
}

/**
 * trackBookingFlowReady: the booking page finished its start-up gate for a
 * fresh start and rendered its first real step.
 *
 * /book holds fresh starts on a loader until GrowthBook features, the signed-in
 * profile and (in the straight-to-service variant) the IP-detected market have
 * settled. This records how long that took and what it waited on, per arm, so
 * the variant's extra wait is a measured number rather than a guess.
 *
 * @param {object} details
 * @param {number} details.waitMs - ms the loader was shown (0 if it never was)
 * @param {string[]} details.waitedOn - which gates were open at the start ('flag' | 'me' | 'market')
 * @param {boolean} details.timedOut - whether a gate gave up rather than resolving
 * @param {boolean} details.straightToService - the arm the client is in
 */
export const trackBookingFlowReady = ({ waitMs, waitedOn, timedOut, straightToService }) => {
  segmentEvent('Booking Flow Ready', {
    waitMs: Math.round(waitMs),
    waitedOn: waitedOn.join(','),
    timedOut: !!timedOut,
    straightToService: !!straightToService
  })
}

/**
 * trackBookingFlowAbandonedDuringLoad: the client left /book while the start-up
 * loader was still showing. Sent with a beacon because the page is unloading
 * (and Segment may not even have loaded yet) — the population this captures is
 * exactly the one that never produces any other event.
 *
 * @param {object} details
 * @param {number} details.elapsedMs - how long the loader had been up
 * @param {string[]} details.waitedOn - which gates were still open
 * @param {boolean} details.straightToService - the arm the client is in
 */
export const trackBookingFlowAbandonedDuringLoad = ({ elapsedMs, waitedOn, straightToService }) => {
  segmentBeaconEvent('Booking Flow Abandoned During Load', {
    elapsedMs: Math.round(elapsedMs),
    waitedOn: waitedOn.join(','),
    straightToService: !!straightToService
  })
}

/**
 * trackBookingServiceFilterChanged: a service-type filter tab ("All", "Hair",
 * "Makeup", ...) was tapped on booking step 2. Until 5.52 this was the one
 * step-2 interaction that produced no event at all, which left the "no
 * interaction" bucket in the straight-to-service experiment unexplained.
 *
 * @param {object} details
 * @param {string} details.serviceType - the filter now selected
 * @param {string} details.previousServiceType - the filter it replaced
 * @param {object} details.market - current booking market
 * @param {boolean} details.straightToService - the arm the client is in
 */
export const trackBookingServiceFilterChanged = ({ serviceType, previousServiceType, market, straightToService }) => {
  segmentEvent('Booking Service Filter Changed', {
    serviceType,
    previousServiceType,
    market: market?.shortName,
    straightToService: !!straightToService
  })
}

/**
 * trackBookingLocationDismissed: the location picker was closed without
 * committing a market/address change. Pairs with `Booking Location Opened`
 * (opened) and `Booking Market Changed` (committed) so the "looked but left it
 * alone" share is measurable.
 *
 * @param {object} details
 * @param {object} details.market - the market still in effect
 * @param {boolean} details.straightToService - the arm the client is in
 */
export const trackBookingLocationDismissed = ({ market, straightToService }) => {
  segmentEvent('Booking Location Dismissed', {
    market: market?.shortName,
    straightToService: !!straightToService
  })
}

/**
 * trackBookingServiceModalClosed: the service detail modal was closed without
 * the service being added. Records what the client had configured before
 * backing out so deliberation (add-ons toggled, instances raised) is visible.
 *
 * @param {object} details
 * @param {object} details.marketService - the service that was open
 * @param {object} details.market - current booking market
 * @param {number} details.instances - instance count at close (1 = untouched)
 * @param {number} details.addOnCount - add-ons selected at close
 * @param {boolean} details.straightToService - the arm the client is in
 */
export const trackBookingServiceModalClosed = ({ marketService, market, instances, addOnCount, straightToService }) => {
  segmentEvent('Booking Service Modal Closed', {
    serviceId: marketService?.service?.id,
    serviceName: marketService?.service?.name,
    serviceType: marketService?.service?.serviceType?.name,
    price: marketService?.price,
    instances: instances ?? 1,
    addOnCount: addOnCount ?? 0,
    market: market?.shortName,
    straightToService: !!straightToService
  })
}

/**
 * trackPriceNotificationDismissed: the "Service prices now include all fees"
 * interstitial (shown on step 2 in markets whose priceDisplay isn't `base`)
 * was dismissed. Lets that interstitial's cost be measured per market/arm.
 *
 * @param {object} details
 * @param {object} details.market - current booking market
 * @param {boolean} details.straightToService - the arm the client is in
 */
export const trackPriceNotificationDismissed = ({ market, straightToService }) => {
  segmentEvent('Price Notification Dismissed', {
    market: market?.shortName,
    straightToService: !!straightToService
  })
}

/**
 * trackBookingFlowScrolled: the client scrolled the /book page past a depth
 * threshold (25/50/75/100 % of the document). Fired once per threshold per
 * step, so a full read of step 2 produces at most four events. Scroll is the
 * engagement signal that GA4 has and Segment did not — the "Engaged on /book"
 * experiment metric can now count readers, not just clickers.
 *
 * @param {object} details
 * @param {number} details.depth - 25 | 50 | 75 | 100
 * @param {number} details.step - booking step being scrolled
 * @param {boolean} details.straightToService - the arm the client is in
 */
export const trackBookingFlowScrolled = ({ depth, step, straightToService }) => {
  segmentEvent('Booking Flow Scrolled', {
    depth,
    step,
    straightToService: !!straightToService
  })
}

/**
 * trackBookingFlowLeft: the client left /book (pagehide) after the start-up
 * gate had cleared. Sent as a beacon because the page is unloading. Together
 * with `Booking Flow Ready` this gives true time-on-flow, and `interacted`
 * separates bounces (looked, never touched anything) from abandons.
 *
 * @param {object} details
 * @param {number} details.timeOnPageMs - ms since Booking Flow Ready
 * @param {number} details.step - step showing at exit
 * @param {boolean} details.interacted - any click/tap/keypress since Ready
 * @param {number} details.scrolledDepth - deepest scroll threshold reached (0 if none)
 * @param {number} details.cartSize - services in the cart at exit
 * @param {boolean} details.straightToService - the arm the client is in
 */
export const trackBookingFlowLeft = ({ timeOnPageMs, step, interacted, scrolledDepth, cartSize, straightToService }) => {
  segmentBeaconEvent('Booking Flow Left', {
    timeOnPageMs: Math.round(timeOnPageMs),
    step,
    interacted: !!interacted,
    scrolledDepth: scrolledDepth || 0,
    cartSize: cartSize || 0,
    straightToService: !!straightToService
  })
}

export const trackOrderCompleted = (order, marketServicesForMarket, serviceRequests, { fromBookedServices = false } = {}) => {
  try {
    if (!order) {
      return
    }

    let data

    // no appointment services meaning its a package, membership, or gift card
    if (!order.appointmentServices) {
      const { item } = order
      data = {
        checkout_id: order.transaction_id,
        subtotal: item.price,
        revenue: item.price,
        total: item.price,
        tax: order.tax,
        tip: order.tip,
        discount: order.promo,
        currency: order.currency,
        type: item.isMembership ? 'membership' : 'package',
        products: formatSegmentProduct(order.item),
        isNewUser: order.isNewUser
      }
    }

    // format data for tracking an appointment
    if (order.appointmentServices) {
      const {
        id,
        totalPrice,
        handlingFee,
        taxAmount,
        tipAmount,
        creditsApplied,
        startDateTime,
        isNewUser,
        address
      } = order

      const _startDateTime = DateTime.fromSeconds(startDateTime, {
        zone: address.market?.timezone
      })

      data = {
        checkout_id: id,
        subtotal: totalPrice,
        revenue: totalPrice,
        value: totalPrice,
        total: totalPrice,
        tax: taxAmount,
        tip: tipAmount,
        fee: handlingFee,
        discount: creditsApplied,
        isNewUser,
        type: 'appointment',
        startDateTime: _startDateTime.toISO(),
        dayOfWeek: _startDateTime.weekdayLong,
        currency: 'USD',
        market: address.market?.shortName,
        ...order.checkoutPaymentContext,
      }

      const products = fromBookedServices
        ? bookedProducts(order.appointmentServices, marketServicesForMarket)
        : serviceRequests.flatMap(serviceRequest => formatServiceRequestAsSegmentProduct(serviceRequest, marketServicesForMarket))

      if (products) {
        data.products = products
      }
    }
        
    // for new users, include campaign context from Branch and/or ad attribution on order events
    let campaignContext
    if (order.isNewUser) {
      try {
        const localStorage = storageMode('localStorage')
        const branchRaw = localStorage.getItem('branch_attribution_for_first_order')
        const adRaw = localStorage.getItem('ad_attribution_for_first_order')
        const firstOrderBranch = branchRaw ? JSON.parse(branchRaw) : null
        const firstOrderAd = adRaw ? JSON.parse(adRaw) : null
        campaignContext = buildCampaignContext(firstOrderBranch, firstOrderAd)
        if (branchRaw) { localStorage.removeItem('branch_attribution_for_first_order') }
        if (adRaw) { localStorage.removeItem('ad_attribution_for_first_order') }
      } catch (e) {
        console.warn('trackOrderCompleted: first-order attribution cleanup failed', e)
      }
    }

    segmentEvent(segmentStandardEvents.PAYMENT_INFO_ENTERED, data, campaignContext)

    segmentEvent(segmentStandardEvents.ORDER_COMPLETED, data, campaignContext)

    if (order.appointmentServices) {
      segmentEvent(`purchase_${order.isNewUser ? 'new' : 'repeat'}`, data, campaignContext)
    }

  } catch (error) {
    console.warn('trackOrderCompleted:', error)
  }
}

/**
  * called in OrderSummary and step 5 of booking flow
  * handles tracking appointment objects or package orderItems
  * formats products and attaches to data object
  * tracks the checkout started screen with data 
  * marketServices only passed when coming from booking flow
  */
export const trackCheckoutStarted = (checkoutInfo, marketServices = [], serviceRequests = [], { fromBookedServices = false } = {}) => {
  try {
    if (!checkoutInfo) {
      return
    }

    const data = {
      // Appointments carry the computed total in finalPriceWithTaxAndTip (finalPrice is unset on the
      // day-view slot shape and back-filled as finalPriceWithTaxAndTip); packages carry finalPrice.
      value: checkoutInfo.finalPriceWithTaxAndTip ?? checkoutInfo.finalPrice,
      revenue: checkoutInfo.totalPrice,
      total: checkoutInfo.totalPrice,
      tax: checkoutInfo.taxAmount,
      tip: checkoutInfo.tipAmount,
      fee: checkoutInfo.handlingFee,
      discount: checkoutInfo.creditsApplied || 0,
      currency: 'USD',
    }

    if (checkoutInfo.actualStartDateTime) {
      data.hoursInAdvance = Math.floor(DateTime.fromSeconds(checkoutInfo.actualStartDateTime).diffNow('hours').hours);
    }

    // when tracking appointment booking
    if (checkoutInfo.appointmentServices) {

      const serviceNames = []
      const serviceTypes = []
      checkoutInfo.appointmentServices.forEach(appointmentService => {
        const serviceId = appointmentService.service?.id || appointmentService.baseServiceId
        const marketService = marketServices.find(ms => ms.service.id === serviceId)
        if (!marketService) {
          return
        }
        const { service } = marketService
        serviceNames.push(service.name)
        serviceTypes.push(service.serviceType.name)
      })

      data.products = fromBookedServices
        ? bookedProducts(checkoutInfo.appointmentServices, marketServices)
        : serviceRequests.flatMap(serviceRequest => formatServiceRequestAsSegmentProduct(serviceRequest, marketServices))

      data.market = checkoutInfo.address.market?.shortName
      data.type = 'Appointment'
      data.serviceNames = serviceNames.join(', ')
      data.serviceTypes = serviceTypes.join(', ')

      const startSeconds = typeof checkoutInfo.startDateTime === 'number'
        ? checkoutInfo.startDateTime
        : checkoutInfo.actualStartDateTime
      const startDateTime = DateTime.fromSeconds(startSeconds, {
        zone: checkoutInfo.address.market?.timezone
      })

      data.startDateTime = startDateTime.toISO()
      data.dayOfWeek = startDateTime.weekdayLong
    }

    // tracking anything but an appointment orderItem will be present
    if (checkoutInfo.orderItem) {
      data.products = formatSegmentProduct(checkoutInfo.orderItem)
      data.type = checkoutInfo.orderItem.isMembership ? 'Membership' : 'Package'
      data.isNewUser = checkoutInfo.isNewUser
    }

    segmentEvent(segmentStandardEvents.CHECKOUT_STARTED, data)
  } catch (error) {
    console.warn('trackCheckoutStarted:', error)
  }
}

// tracking for viewing full product lists (packages, services)
export const trackProductListViewed = (category, items) => {
  try {
    if (!(category && items)) {
      return
    }
    segmentEvent(segmentStandardEvents.PRODUCT_LIST_VIEWED, {
      category,
      products: formatSegmentProduct(items)
    })
  } catch (error) {
    console.warn('trackProductListViewed:', error)
  }
}

// tracking for viewing a product details
export const trackProductViewed = product => {
  try {
    segmentEvent(segmentStandardEvents.PRODUCT_VIEWED, formatSegmentProduct(product)[0])
  } catch (error) {
    console.warn('trackProductViewed:', error)
  }
}

// tracking for membership join buttons
export const trackProductClicked = product => {
  try {
    segmentEvent(segmentStandardEvents.PRODUCT_CLICKED, formatSegmentProduct(product)[0])
  } catch (error) {
    console.warn('trackProductClicked:', error)
  }
}

// tracking for a public /services/<slug> service detail page view (name + slug)
export const trackServiceDetailViewed = ({ serviceId, serviceName, serviceSlug }) => {
  try {
    segmentEvent('Service Detail Viewed', {
      service_id: serviceId,
      service_name: serviceName,
      service_slug: serviceSlug,
    })
  } catch (error) {
    console.warn('trackServiceDetailViewed:', error)
  }
}

// tracking for the hero "Book Service" CTA on a service detail page
export const trackServiceDetailBookClicked = ({ serviceId, serviceName, serviceSlug, market }) => {
  try {
    segmentEvent('Service Detail Book Clicked', {
      service_id: serviceId,
      service_name: serviceName,
      service_slug: serviceSlug,
      market,
    })
  } catch (error) {
    console.warn('trackServiceDetailBookClicked:', error)
  }
}

// tracking for a cross-sell card click on a service detail page (from-service -> to-service)
export const trackServiceDetailCrossSellClicked = ({ fromSlug, toSlug, toServiceName }) => {
  try {
    segmentEvent('Service Detail Cross Sell Clicked', {
      from_service_slug: fromSlug,
      to_service_slug: toSlug,
      to_service_name: toServiceName,
    })
  } catch (error) {
    console.warn('trackServiceDetailCrossSellClicked:', error)
  }
}

// tracking for adding product to cart
export const trackProductAdded = (product, instances, addOnMarketServices) => {
  try {
    segmentEvent(segmentStandardEvents.PRODUCT_ADDED, formatSegmentProduct({ ...product, instances, addOnMarketServices })[0])
  } catch (error) {
    console.warn('trackProductAdded:', error)
  }
}

//tracking for removing a product from cart
export const trackProductRemoved = (marketService, serviceRequest) => {
  try {
    segmentEvent(segmentStandardEvents.PRODUCT_REMOVED, formatServiceRequestAsSegmentProduct(serviceRequest, [marketService])[0])
  } catch (error) {
    console.warn('trackProductRemoved:', error)
  }
}

/** Returns analytics-safe payment dimensions without exposing Stripe identifiers. */
export const checkoutPaymentContext = (selectedId, availability) => ({
  paymentMethod: selectedId === PLATFORM_PAY ? 'platformPay' : selectedId ? 'card' : 'none',
  walletType: selectedId === PLATFORM_PAY
    ? (availability?.applePay ? 'applePay' : availability?.googlePay ? 'googlePay' : null)
    : null,
})

/** Records the resolved payment UI once per distinct presentation on a step visit. */
export const trackPaymentOptionsViewed = properties => segmentEvent('Payment Options Viewed', properties)

/** Records default versus user payment choices; methods are normalized by the caller. */
export const trackPaymentMethodSelected = properties => segmentEvent('Payment Method Selected', properties)

/** Records credit-claim outcomes, including URL applications and responsive placement. */
export const trackPromoCodeResult = (success, properties) =>
  segmentEvent(success ? 'Promo Code Applied' : 'Promo Code Failed', properties)

/** Records explicit policy opens (including reopens), never closes or initial rendering. */
export const trackCheckoutPolicyOpened = properties => segmentEvent('Checkout Policy Opened', properties)

export const trackPaymentInfoEntered = () => {
  try {
    segmentEvent(segmentStandardEvents.PAYMENT_INFO_ENTERED)
  } catch (error) {
    console.warn('trackPaymentInfoEntered:', error)
  }
}


/**
 * formatServiceRequestAsSegmentProduct - formats a service request for segment
 *
 * @param {*} serviceRequest
 * @param {*} marketServices
 * @return {*} 
 */
const formatServiceRequestAsSegmentProduct = (serviceRequest, marketServices) => {
  if (!(serviceRequest && marketServices)) {
    return {}
  }
  const marketService = marketServices.find(ms => ms.service.id === serviceRequest.serviceId)
  const addOnMarketServices = marketService?.addOnMarketServices?.filter(addOnMarketService => serviceRequest.addOnServiceIds.includes(addOnMarketService.service.id))
  const price = marketService?.price + (addOnMarketServices?.reduce((total, addOnMarketService) => total + addOnMarketService.addOnPrice, 0) || 0)
  return formatSegmentProduct({ ...serviceRequest, ...marketService, addOnMarketServices, price })
}

/**
 * bookedProducts: build the Segment `products` array from an appointment's actually-booked services
 * rather than the original cart, so a day-view partial/alternate booking reports what it booked (e.g. a
 * skipped add-on is gone). Tolerant of the client shape (baseServiceId/addOnServiceIds) and any server
 * shape (serviceId/service.id, addOnServices[]).
 * @param {Array} appointmentServices the booked appointment's appointmentServices
 * @param {Array} marketServices market services for id -> name/price lookup
 * @returns {Array} Segment products
 */
const bookedProducts = (appointmentServices = [], marketServices = []) =>
  (appointmentServices || [])
    .map(appointmentService => ({
      serviceId: appointmentService.baseServiceId || appointmentService.serviceId || appointmentService.baseService?.id || appointmentService.service?.id,
      addOnServiceIds: appointmentService.addOnServiceIds || (appointmentService.addOnServices || []).map(addOn => addOn.id),
    }))
    .flatMap(serviceRequest => formatServiceRequestAsSegmentProduct(serviceRequest, marketServices))

/**
 * formatSegmentProduct - formats a product for segment
 *
 * @param {*} products
 * @return {*} 
 */
export const formatSegmentProduct = products => {
  try {
    if (!products) {
      return []
    }

    return (Array.isArray(products) ? products : [products]).map(product => ({
      product_id: product.service ? product.service.id : product.id,
      name: product.service ? product.service.name : product.name,
      price: product.price,
      category: product.service ? product.service.serviceType.name : product.isMembership ? 'Membership' : 'Package',
      quantity: product.instances || 1,
      variant: product.addOnMarketServices?.map(addOnMarketService => addOnMarketService.service.addOnName).join(', '),
      add_ons: product.addOnMarketServices?.map(addOnMarketService => addOnMarketService.service.addOnName),
    }))
  } catch (error) {
    console.warn('formatSegmentProduct:', error)
    return [];
  }
}

/**
 * trackError - tracks an error to segment
 *
 * @param {*} title
 * @param {*} error
 */
export const trackError = (title, error) => {
  if (!title || !error) {
    return
  }

  segmentEvent('Error', {
    title,
    message: error.message,
    error,
  })
}

// Availability Search Tracking Events 
const segmentCustomEvents = {
  AVAILABILITY_SEARCH_RESULT_FOUND: 'Availability Search Result Found',
  AVAILABILITY_SEARCH_RESULT_NOT_FOUND: 'Availability Search Result Not Found',
  AVAILABILITY_SEARCH_RESULT_ALTERNATE_TIME_FOUND: 'Availability Search Result Alternate Time Found',
  AVAILABILITY_SEARCH_RESULT_WITHOUT_ADDONS_FOUND: 'Availability Search Result Without Addons Found',
  AVAILABILITY_SEARCH_RESULT_WITHOUT_SERVICE_FOUND: 'Availability Search Result Without Service Found',
  AVAILABILITY_SEARCH_RESULT_ALTERNATE_SERVICE_FOUND: 'Availability Search Result Alternate Service Found',
  AVAILABILITY_SEARCH_ERROR: 'Availability Search Error',
}

// tracking for step 3 of booking flow: tracks market services from service requests
const trackAvailabilitySearchSegment = (eventName, startDateTime, serviceRequests, marketServices, address, extraProps) => {
  try {
    if (!(startDateTime && serviceRequests && marketServices && marketServices.length)) {
      return
    }

    const products = serviceRequests.flatMap(serviceRequest => formatServiceRequestAsSegmentProduct(serviceRequest, marketServices))

    startDateTime = startDateTime.toISO ? startDateTime : DateTime.fromJSDate(startDateTime)

    segmentEvent(eventName, {
      startDateTime: startDateTime.toISO(),
      hoursInAdvance: Math.floor(startDateTime.diffNow('hours').hours),
      products,
      // address of the search so no-availability can be analyzed by location (zip is the key field)
      ...(address && {
        zip: address.zip,
        city: address.city,
        state: address.state,
        street: address.street,
      }),
      ...extraProps,
    })
  } catch (error) {
    console.warn('trackAvailabilitySearchSegment:', error)
  }
}

export const trackAvailabilitySearch = (startDateTime, serviceRequests, marketServices, address) => {
  trackAvailabilitySearchSegment(segmentStandardEvents.AVAILABILITY_SEARCH, startDateTime, serviceRequests, marketServices, address)
}

export const trackAvailabilitySearchResultFound = (startDateTime, serviceRequests, marketServices, address) => {
  trackAvailabilitySearchSegment(segmentCustomEvents.AVAILABILITY_SEARCH_RESULT_FOUND, startDateTime, serviceRequests, marketServices, address)
}

export const trackAvailabilitySearchResultNotFound = (startDateTime, serviceRequests, marketServices, address) => {
  trackAvailabilitySearchSegment(segmentCustomEvents.AVAILABILITY_SEARCH_RESULT_NOT_FOUND, startDateTime, serviceRequests, marketServices, address)
}

export const trackAvailabilitySearchResultNoAddonsFound = (startDateTime, serviceRequests, marketServices, address) => {
  trackAvailabilitySearchSegment(segmentCustomEvents.AVAILABILITY_SEARCH_RESULT_WITHOUT_ADDONS_FOUND, startDateTime, serviceRequests, marketServices, address)
}

export const trackAvailabilitySearchAlternateTimeFound = (startDateTime, serviceRequests, marketServices, address) => {
  trackAvailabilitySearchSegment(segmentCustomEvents.AVAILABILITY_SEARCH_RESULT_ALTERNATE_TIME_FOUND, startDateTime, serviceRequests, marketServices, address)
}

export const trackAvailabilitySearchWithoutServiceFound = (startDateTime, serviceRequests, marketServices, address) => {
  trackAvailabilitySearchSegment(segmentCustomEvents.AVAILABILITY_SEARCH_RESULT_WITHOUT_SERVICE_FOUND, startDateTime, serviceRequests, marketServices, address)
}

export const trackAvailabilitySearchAlternateServiceFound = (startDateTime, serviceRequests, marketServices, address) => {
  trackAvailabilitySearchSegment(segmentCustomEvents.AVAILABILITY_SEARCH_RESULT_ALTERNATE_SERVICE_FOUND, startDateTime, serviceRequests, marketServices, address)
}

/**
 * dayViewSearchProps: shared property shape for the day-view (full-day) availability search event.
 * Carries `type: 'full-day'` so it can be split from the classic single-time search, plus the searched
 * day, market shortName, and the searched address (zip is the key field for no-availability-by-location).
 * @param {object} args { market, date, address }
 * @returns {object}
 */
const dayViewSearchProps = ({ market, date, address }) => ({
  type: 'full-day',
  market: market?.shortName,
  date,
  ...(address && { zip: address.zip, city: address.city, state: address.state, street: address.street }),
})

/**
 * dayViewServiceDescriptors: resolve the booking's serviceRequests into the { serviceNames, serviceTypes }
 * string arrays the mantis Full Day funnel events carry (serviceNames per service, serviceTypes deduped).
 * @param {Array} serviceRequests
 * @param {Array} marketServices
 * @returns {{ serviceNames: string[], serviceTypes: string[] }}
 */
const dayViewServiceDescriptors = (serviceRequests = [], marketServices = []) => {
  const resolved = serviceRequests
    .map(sr => marketServices.find(ms => ms.service?.id === sr.serviceId)?.service)
    .filter(Boolean)
  return {
    serviceNames: resolved.map(service => service.name),
    serviceTypes: [...new Set(resolved.map(service => service.serviceType?.name).filter(Boolean))],
  }
}

/**
 * trackDayViewAvailabilitySearch: a day-view (full-day) availability search was initiated for a day.
 * Emits the shared `Availability Search` event, discriminated by `type: 'full-day'` to match mantis.
 * Carries the same `products` array the classic search emits (via marketServices) so both arms of the
 * event share one schema and can be joined to service/product-level availability metrics.
 * @param {object} args { market, date, serviceRequests, address, marketServices }
 */
export const trackDayViewAvailabilitySearch = ({ market, date, serviceRequests = [], address, marketServices = [] }) => {
  const props = {
    ...dayViewSearchProps({ market, date, address }),
    products: serviceRequests.flatMap(sr => formatServiceRequestAsSegmentProduct(sr, marketServices)),
  }
  segmentEvent(segmentStandardEvents.AVAILABILITY_SEARCH, props)
}

/**
 * trackDayViewAvailabilitySearchError: a day-view (full-day) availability search ended in an error
 * rather than an outcome. Without this, a failed full-day request leaves an `Availability Search` with
 * no follow-up — indistinguishable from a shopper who navigated away mid-search — so the full-day error
 * rate would be undercounted, unlike the classic search's catch path. `isUserFacing` splits a server
 * message from an unexpected client-side bug.
 * @param {object} args { market, date, serviceRequests, address, marketServices, errorMessage, isUserFacing }
 */
export const trackDayViewAvailabilitySearchError = ({
  market, date, serviceRequests = [], address, marketServices = [], errorMessage, isUserFacing,
}) => {
  const props = {
    ...dayViewSearchProps({ market, date, address }),
    products: serviceRequests.flatMap(sr => formatServiceRequestAsSegmentProduct(sr, marketServices)),
    errorMessage,
    isUserFacing,
  }
  segmentEvent(segmentCustomEvents.AVAILABILITY_SEARCH_ERROR, props)
}

/**
 * trackDayViewDateSelected: the shopper picked a different day in the day-view selector. Mirrors the
 * mantis `Full Day Date Selected` funnel event ({ date, zip, serviceNames, serviceTypes }).
 * @param {object} args { date, address, serviceRequests, marketServices }
 */
export const trackDayViewDateSelected = ({ date, address, serviceRequests = [], marketServices = [] }) => {
  const props = {
    date,
    zip: address?.zip,
    ...dayViewServiceDescriptors(serviceRequests, marketServices),
  }
  segmentEvent('Full Day Date Selected', props)
}

/**
 * trackDayViewSlotSelected: the shopper tapped a slot in the day-view grid. Emits the mantis
 * `Full Day Time Slot Selected` event; `isAlternate` distinguishes a reduced-service (partial) slot.
 * @param {object} args { time, isAlternate }
 */
export const trackDayViewSlotSelected = ({ time, isAlternate = false }) => {
  segmentEvent('Full Day Time Slot Selected', { time, hasPreferredProviders: false, isAlternate })
}

/**
 * trackAvailabilitySearchError: an availability search ended in an error rather than an outcome.
 *
 * Every other outcome event fires only on a successful round trip, so a search that throws
 * used to leave an `Availability Search` with no follow-up event — indistinguishable in
 * analytics from a customer who closed the tab mid-request. This makes the error path
 * countable, split by whether the message came from the server (`isUserFacing`) or from an
 * unexpected client-side bug.
 *
 * @param {*} startDateTime searched start time
 * @param {Array} serviceRequests requested services
 * @param {Array} marketServices market services, for product formatting
 * @param {object} address searched address
 * @param {object} errorProps { errorMessage, isUserFacing }
 */
export const trackAvailabilitySearchError = (startDateTime, serviceRequests, marketServices, address, errorProps) => {
  trackAvailabilitySearchSegment(segmentCustomEvents.AVAILABILITY_SEARCH_ERROR, startDateTime, serviceRequests, marketServices, address, errorProps)
}

/**
 * getAuthErrorMessage - extracts the human-readable Starbase error message. Cicada's
 * axios-based makeSignedAPICall rejects with the raw axios error, whose real message lives
 * under response.data.errors[0].message (its top-level `message` is just "Request failed
 * with status code 400"). Mirrors mantis's helper of the same name.
 * @param {any} error
 * @returns {string}
 */
export const getAuthErrorMessage = (error) =>
  error?.response?.data?.errors?.[0]?.message || error?.message || 'unknown'

/**
 * classifyAuthError - buckets a caught auth error into a coarse `errorType`, attached to the
 * auth failure events ('Login Code Failed', 'One Time Passcode Failed', 'Sign Up Failed',
 * 'Account Selection Failed'). Kept in sync with mantis (app).
 *
 * Grounded in the ACTUAL Starbase responses, not HTTP conventions: Starbase returns 400 for
 * almost every auth failure, so we classify by the returned message string, not the status.
 * Wrong vs. expired OTP are indistinguishable (both 'Invalid or expired code') → a single
 * 'invalid_code'. The raw message is also emitted on the event, so imperfect bucketing is
 * recoverable. If Starbase reworks these strings, update the substrings here AND in mantis.
 *
 * @param {any} error
 * @returns {'invalid_code'|'rate_limited'|'service_unavailable'|'unauthorized'|'account_locked'|'email_exists'|'validation'|'unsupported_phone'|'canceled'|'network'|'unknown'}
 */
export const classifyAuthError = (error) => {
  if (error?.code === 'ERR_REQUEST_CANCELED') { return 'canceled' }
  const message = (getAuthErrorMessage(error) || '').toLowerCase()
  if (message.includes('invalid or expired code')) { return 'invalid_code' }
  if (message.includes('service temporarily unavailable')) { return 'service_unavailable' }
  if (message.includes('too many login attempts') || message.includes('rate limit exceeded')) { return 'rate_limited' }
  if (message.includes('already have an account') || message.includes('already exists')) { return 'email_exists' }
  if (message.includes('account locked')) { return 'account_locked' }
  if (
    message.includes('incorrect password') ||
    message.includes('user not found') ||
    message.includes('username and password') ||
    message.includes('unauthorized') ||
    message.includes('role permissions')
  ) { return 'unauthorized' }
  if (message.includes('unsupported phone')) { return 'unsupported_phone' }
  if (
    message.includes('valid email') ||
    message.includes('first name') ||
    message.includes('last name') ||
    message.includes('eight characters') ||
    message.includes('creating your account') ||
    // provider (Google/Apple) proof rejections that are expected user friction: an unverified
    // provider email (e.g. Apple Hide My Email / relay) or a stale signup proof the user can
    // retry by re-authorizing. NOTE: 'provider email does not match account email' is
    // intentionally NOT matched — it should be impossible in the verified flow, so it stays
    // 'unknown' as a bug/tampering signal. Mirrors mantis's classifier.
    message.includes('email is not verified') ||
    message.includes('social signup token')
  ) { return 'validation' }
  if (
    error?.code === 'ERR_NETWORK' ||
    (!error?.response && (message.includes('network') || message.includes('timeout') || message === 'unknown'))
  ) { return 'network' }
  return 'unknown'
}

// stash the chosen sign-in method ('phone' | 'email') so the subsequent
// 'Signed In' event — which can fire later (e.g. after the OTP exchange and a
// page navigation, not at the moment of submission) — can be attributed to the
// method the user actually used.
const SIGNIN_TYPE_STORAGE_KEY = 'signin_type'

// fires when a sign-in is submitted (the phone/email identifier is submitted to
// request an OTP). `type` is 'phone' or 'email' so attempts can be compared by
// method. Emitted flat as `{ type }` to match mantis (app).
export const trackSigninStarted = (type) => {
  try {
    storageMode('sessionStorage').setItem(SIGNIN_TYPE_STORAGE_KEY, type)
    segmentEvent('Signin Started', { type })
  } catch (error) {
    console.warn('trackSigninStarted:', error)
  }
}

export const trackSignedIn = (email, id) => {
  try {
    const storage = storageMode('sessionStorage')
    const type = storage.getItem(SIGNIN_TYPE_STORAGE_KEY)
    // clear so only the first 'Signed In' after a login carries the method —
    // later page-load identifies shouldn't reuse a stale type
    if (type) {
      storage.removeItem(SIGNIN_TYPE_STORAGE_KEY)
    }
    // flat properties (matches mantis); groupId goes in Segment's real context via
    // the 3rd `options` arg, not as a nested property
    segmentEvent(
      segmentStandardEvents.SIGNED_IN,
      { username: email, ...(type && { type }) },
      { context: { groupId: id } },
    )
  } catch (error) {
    console.warn('trackSignedIn:', error)
  }
}

/**
 * trackSignedUp - tracks a completed account creation.
 *
 * @param {object} properties - user props (email/name/phone/username)
 * @param {string} id - the new user id (rides in Segment context.groupId)
 * @param {object} branchAttribution
 * @param {object} adAttribution
 * @param {string} type - the method the account was created with ('email' | 'phone'),
 *   emitted as the `type` property so signups can be compared by method (matches mantis)
 */
export const trackSignedUp = (properties, id, branchAttribution, adAttribution, type) => {
  try {
    const campaignContext = buildCampaignContext(branchAttribution, adAttribution)
    // flat properties (matches mantis); groupId + campaign go in the 3rd `options` arg so
    // groupId lands in Segment's real context and campaign context merges correctly
    segmentEvent(
      segmentStandardEvents.SIGNED_UP,
      {
        ...properties,
        ...(type && { type }),
        ...(branchAttribution && { referring_link: branchAttribution['~referring_link'] }),
      },
      { context: { groupId: id, ...(campaignContext?.context) } },
    )
  } catch (error) {
    console.warn('trackSignedUp:', error)
  }
}

// --- Checkout gratuity tracking (matches mantis) ---

export const trackCheckoutTipRateSelected = (selectedTipRate, previousTipRate, totalPrice) => {
  segmentEvent('Checkout Tip Rate Selected', {
    selectedTipRate,
    previousTipRate,
    totalPrice,
  })
}

export const trackCheckoutTipInCashToggled = (tippedInCash, totalPrice) => {
  segmentEvent('Checkout Tip In Cash Toggled', {
    tippedInCash,
    totalPrice,
  })
}

/**
 * Tracks a tap on a "Download The App" CTA. Segment-only.
 * @param {object} params
 * @param {string} params.placement where the CTA lives (e.g. 'booking_confirmation')
 * @param {string} [params.market] market shortName for context
 */
export const trackAppDownloadClicked = ({ placement, market } = {}) => {
  segmentEvent('App Download Clicked', { placement, market })
}

// --- Survey lifecycle tracking (matches mantis) ---

export const trackSurveyViewed = ({ appointmentId, isMultiService }) => {
  segmentEvent('Survey Viewed', { appointmentId, isMultiService })
}

export const trackSurveySubmitted = ({ appointmentId, isMultiService, answerCount }) => {
  segmentEvent('Survey Submitted', { appointmentId, isMultiService, answerCount })
}

export const trackSurveyQuestionAnswered = ({ appointmentId, questionId, questionType, rating }) => {
  segmentEvent('Survey Question Answered', { appointmentId, questionId, questionType, rating })
}

export const trackSurveySubmitTapped = ({ appointmentId, isMultiService }) => {
  segmentEvent('Survey Submit Tapped', {
    appointmentId,
    ...(isMultiService && { isMultiService: true }),
  })
}

export const trackSurveyNextTapped = ({ appointmentId }) => {
  segmentEvent('Survey Next Tapped', { appointmentId })
}

// --- Survey gratuity tracking (matches mantis) ---

export const trackSurveyGratuityPresented = ({
  appointmentId,
  isMultiService,
  tipSelectorShown,
  providerCount,
  providers,
}) => {
  segmentEvent('Survey Gratuity Presented', {
    appointmentId,
    isMultiService,
    tipSelectorShown,
    providerCount,
    providers,
  })
}

export const trackSurveyTipRateSelected = ({
  appointmentId,
  selectedTipRate,
  originalTipRate,
  isPartialTipper,
  servicePrice,
  providerId,
  isMultiService,
}) => {
  segmentEvent('Survey Tip Rate Selected', {
    appointmentId,
    selectedTipRate,
    originalTipRate,
    ...(isMultiService
      ? { providerId, isMultiService: true }
      : { isPartialTipper, servicePrice }),
  })
}

export const trackSurveyTipInCashToggled = ({
  appointmentId,
  tippedInCash,
  providerId,
  isMultiService,
}) => {
  segmentEvent('Survey Tip In Cash Toggled', {
    appointmentId,
    tippedInCash,
    ...(isMultiService && { providerId, isMultiService: true }),
  })
}

export const trackSurveyTipDollarAmountSelected = ({
  appointmentId,
  additionalTipDollar,
  servicePrice,
  providerId,
  isMultiService,
}) => {
  segmentEvent('Survey Tip Dollar Amount Selected', {
    appointmentId,
    additionalTipDollar,
    servicePrice,
    ...(isMultiService && { providerId, isMultiService: true }),
  })
}

export const trackSurveyAdjustTipOpened = ({
  appointmentId,
  currentTipDollarAmount,
  servicePrice,
}) => {
  segmentEvent('Survey Adjust Tip Opened', {
    appointmentId,
    currentTipDollarAmount,
    servicePrice,
  })
}

export const trackSurveyAdjustTipSaved = ({
  appointmentId,
  previousTipDollarAmount,
  newTipDollarAmount,
  newTipRate,
  didTipInCash,
  servicePrice,
}) => {
  segmentEvent('Survey Adjust Tip Saved', {
    appointmentId,
    previousTipDollarAmount,
    newTipDollarAmount,
    newTipRate,
    didTipInCash,
    servicePrice,
  })
}

export const trackSurveyAdjustTipCancelled = ({
  appointmentId,
}) => {
  segmentEvent('Survey Adjust Tip Cancelled', {
    appointmentId,
  })
}
